import { useState } from "react"
import Icon from "./Icon"
import { formatMoney, getMonthName, getNairobiYear } from "../utils/format"

// Shared "at a glance" monthly summary card used across modules (Rentals uses
// its own inline version; Groups and Utilities use this one). It renders a
// gradient header with a month/year picker, an optional segmented control to
// switch between role sections (e.g. Member / Admin), and a responsive grid of
// stat tiles plus an optional key/value facts box.
//
// Data shape (per section):
//   { key, label, stats: [{ label, value, money?, tone? }], facts?: [{ label, value }] }
// tone: 'green' | 'red' | 'purple' | 'sky' | 'teal' | 'amber' | 'slate'
// renderStatAction({ activeSection, stat }) can return a small control (for
// example a button) that is shown inside that stat's own tile.

const TONE_TEXT = {
  green: "text-emerald-600",
  red: "text-rose-600",
  purple: "text-purple-600",
  sky: "text-sky-600",
  teal: "text-teal-600",
  amber: "text-amber-600",
  slate: "text-slate-900",
}

const ACCENTS = {
  sky: {
    header: "from-sky-500 to-blue-600",
    button: "bg-white text-sky-700 hover:bg-sky-50",
    ring: "focus:ring-sky-400",
    seg: "bg-sky-600 text-white",
    segIdle: "text-sky-700 hover:bg-sky-50",
    chip: "bg-white/15 text-white",
  },
  teal: {
    header: "from-emerald-600 to-teal-600",
    button: "bg-white text-teal-700 hover:bg-teal-50",
    ring: "focus:ring-teal-400",
    seg: "bg-teal-600 text-white",
    segIdle: "text-teal-700 hover:bg-teal-50",
    chip: "bg-white/15 text-white",
  },
  emerald: {
    header: "from-emerald-500 to-green-600",
    button: "bg-white text-emerald-700 hover:bg-emerald-50",
    ring: "focus:ring-emerald-400",
    seg: "bg-emerald-600 text-white",
    segIdle: "text-emerald-700 hover:bg-emerald-50",
    chip: "bg-white/15 text-white",
  },
}

function formatValue(stat) {
  if (stat.value == null) return stat.money ? formatMoney(0) : "0"
  if (stat.money) return formatMoney(Number(stat.value) || 0)
  return stat.value
}

function MonthlySummaryCard({
  title = "Monthly Summary",
  subtitle,
  icon = "bar-chart-line-fill",
  accent = "sky",
  month,
  year,
  period,
  loading = false,
  onMonthChange,
  onYearChange,
  onUpdate,
  sections = [],
  emptyText = "No activity for this period.",
  onViewTransactions,
  renderExtraTile,
  renderStatAction,
}) {
  const theme = ACCENTS[accent] || ACCENTS.sky
  const [activeKey, setActiveKey] = useState(sections[0]?.key || null)
  const activeSection =
    sections.find((s) => s.key === activeKey) || sections[0] || null
  const extraTile =
    activeSection && typeof renderExtraTile === "function"
      ? renderExtraTile({ activeSection, month, year, period })
      : null

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.header} px-4 py-3 text-white sm:px-5 sm:py-4`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
              <Icon name={icon} size="1.1rem" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-black leading-tight sm:text-lg">{title}</h3>
              {subtitle && <p className="text-[11px] leading-snug text-white/80">{subtitle}</p>}
            </div>
          </div>
          {period && (
            <span className={`shrink-0 whitespace-nowrap rounded-lg px-2.5 py-1 text-[11px] font-semibold ${theme.chip}`}>
              {period}
            </span>
          )}
        </div>

        {/* Month / Year controls */}
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-[10px] font-semibold uppercase tracking-wide text-white/80">
            Month
            <select
              value={month}
              onChange={(e) => onMonthChange?.(parseInt(e.target.value, 10))}
              className={`mt-1 rounded-lg border-0 bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-slate-800 shadow-sm outline-none focus:ring-2 ${theme.ring}`}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{getMonthName(m)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-[10px] font-semibold uppercase tracking-wide text-white/80">
            Year
            <select
              value={year}
              onChange={(e) => onYearChange?.(parseInt(e.target.value, 10))}
              className={`mt-1 rounded-lg border-0 bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-slate-800 shadow-sm outline-none focus:ring-2 ${theme.ring}`}
            >
              {Array.from({ length: 5 }, (_, i) => getNairobiYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => onUpdate?.()}
            disabled={loading}
            className={`rounded-lg ${theme.button} px-3.5 py-1.5 text-xs font-bold shadow-sm transition-colors disabled:opacity-60`}
          >
            {loading ? "Loading…" : "Update"}
          </button>
        </div>
      </div>

      {/* Section switcher */}
      {sections.length > 1 && (
        <div className="flex gap-1 border-b border-slate-100 bg-slate-50 px-2 py-2">
          {sections.map((section) => (
            <button
              key={section.key}
              type="button"
              onClick={() => setActiveKey(section.key)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                activeSection?.key === section.key ? theme.seg : theme.segIdle
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="p-3 sm:p-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : !activeSection || (activeSection.stats?.length ?? 0) === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">{emptyText}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {activeSection.stats.map((stat, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:bg-slate-100/70"
              >
                <p className="text-[11px] font-medium text-slate-500">{stat.label}</p>
                <p className={`mt-1 text-base font-black leading-tight ${TONE_TEXT[stat.tone] || TONE_TEXT.slate}`}>
                  {formatValue(stat)}
                </p>
                {stat.hint && <p className="mt-0.5 text-[10px] text-slate-400">{stat.hint}</p>}
                {typeof renderStatAction === "function" ? renderStatAction({ activeSection, stat }) : null}
              </div>
            ))}

            {extraTile}

            {Array.isArray(activeSection.facts) && activeSection.facts.length > 0 && (
              <div className="col-span-2 rounded-xl border border-slate-100 bg-slate-50 p-3 sm:col-span-1">
                <div className="space-y-1 text-xs leading-tight">
                  {activeSection.facts.map((fact, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-500">{fact.label}</span>
                      <span className="font-bold text-slate-900">{fact.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {onViewTransactions && !loading && activeSection && (
          <button
            type="button"
            onClick={() => onViewTransactions(activeSection.key)}
            className={`mt-3 flex w-full items-center justify-center gap-2 rounded-xl ${theme.seg} px-3 py-2.5 text-sm font-bold shadow-sm transition-opacity hover:opacity-90`}
          >
            <Icon name="list-ul" size="0.9rem" />
            Transactions
          </button>
        )}
      </div>
    </div>
  )
}

export default MonthlySummaryCard
